import { forwardRef } from 'react';

import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { InputAdornment, TextField, SxProps, Theme } from '@mui/material';
import { FieldValues, ControllerRenderProps } from 'react-hook-form';
import { IMaskInput } from 'react-imask';

import { BackrsFormField } from './types';

// we use any here to get the correct match with IMaskInputProps
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PhoneMask = forwardRef<HTMLInputElement, any>(function PhoneMask(props, ref) {
  const { onChange, ...rest } = props;

  return (
    <IMaskInput
      {...rest}
      mask="(000) 000-0000"
      inputRef={ref}
      unmask={true}
      onAccept={(value: string) => onChange({ target: { value } })}
    />
  );
});

type BackrsFormTextFieldProps = Readonly<{
  field: BackrsFormField;
  groupedFields: Record<string, BackrsFormField[]>;
  index: number;
  controllerField: ControllerRenderProps<FieldValues, string>;
  error?: string;
  isDisabled?: boolean;
}>;

export const BackrsFormTextField = ({
  field,
  groupedFields,
  index,
  error,
  controllerField,
  isDisabled,
}: BackrsFormTextFieldProps) => {
  const isTel = field.type === 'tel';

  return (
    <TextField
      id={field.name}
      disabled={field.disabled || isDisabled}
      name={field.name}
      label={field.label}
      placeholder={field.placeholder}
      type={isTel ? 'text' : field.type} // override native 'tel' for masking
      InputProps={{
        inputComponent: isTel ? PhoneMask : undefined,
        inputProps: isTel ? { onChange: controllerField.onChange } : undefined,
        startAdornment: field.customStartAdornment ? (
          <InputAdornment position="start" sx={customStartAdornment}>
            {field.customStartAdornment}
          </InputAdornment>
        ) : field.icon?.type !== 'RIGHT_ICON' && field.icon?.leftIcon ? (
          <InputAdornment
            position="start"
            onClick={field.icon?.onLeftIconClick}
            sx={{ cursor: field.icon?.onLeftIconClick ? 'pointer' : 'default' }}
          >
            <AppIcon
              iconName={field.icon?.leftIcon}
              diameter={field.icon?.leftIconSize || '16px'}
              color={field.icon?.leftIconColor || 'Colors/Base/Black'}
            />
          </InputAdornment>
        ) : undefined,
        endAdornment:
          field.icon?.type !== 'LEFT_ICON' && field.icon?.rightIcon ? (
            <InputAdornment
              position="end"
              onClick={field.icon?.onRightIconClick}
              sx={{ cursor: field.icon?.onRightIconClick ? 'pointer' : 'default' }}
            >
              <AppIcon
                iconName={field.icon?.rightIcon}
                diameter={field.icon?.rightIconSize || '16px'}
                color={field.icon?.rightIconColor || 'Colors/Base/Black'}
              />
            </InputAdornment>
          ) : undefined,
      }}
      autoComplete={field.autoComplete}
      autoFocus={field.autoFocus}
      value={controllerField.value ?? ''}
      onChange={!isTel ? controllerField.onChange : undefined}
      error={!!error}
      helperText={error}
      sx={textFieldStyle(
        index === Object.entries(groupedFields).length - 1,
        field.divider || false,
      )}
    />
  );
};

const customStartAdornment: SxProps<Theme> = ({ backrs }) => ({
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  paddingRight: backrs.spacing['spacing-md'],
  height: backrs.spacing['spacing-6xl'],
  maxHeight: 'unset',
  userSelect: 'none',
  cursor: 'default',
});

const textFieldStyle =
  (isLastItem: boolean, divider: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    mb: {
      xs: backrs.spacing['spacing-md'],
      sm: backrs.spacing[isLastItem ? 'spacing-md' : 'spacing-2xl'],
    },
    width: '100%',
    ...(divider && {
      mb: backrs.spacing['spacing-none'],
    }),
    '& .MuiInputBase-adornedStart': {
      paddingY: `${backrs.spacing['spacing-none']} !important`,
    },

    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      opacity: 0.0000001, // Hide visually, but keep it clickable
      position: 'absolute',
      right: '-16px',
      width: '100%',
      cursor: 'pointer',
      zIndex: 1000,
    },
  });
